import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useFetchDayOff } from '../../hooks/useFetchDayOff';
import { useInsertDocument } from '../../hooks/useInsertDocument';
import { useDeleteDocument } from '../../hooks/useDeleteDocument';
import { useFetchEnabledHours } from '../../hooks/useFetchEnabledHours';
import { buildDayOffId } from '../../utilities/appointmentSlots';
import { formatLocalDateDisplay, formatLocalDateInput } from '../../utilities/dateUtils';
import { useToast } from '../ui';
import { Button } from '../ui';

const PROFESSIONALS = ['Carlos', 'Donizete'];

export default function DayOff() {
  const [professional, setProfessional] = useState('');
  const [date, setDate] = useState('');
  const [dayOffDate, setDayOffDate] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { documents } = useFetchDayOff('day-off', dayOffDate);
  const { documents: appointmentsOnDay } = useFetchEnabledHours('transactions', professional, date);
  const { setDocument } = useInsertDocument('day-off');
  const { deleteData } = useDeleteDocument();
  const toast = useToast();

  const handleProfessionalChange = (e) => {
    setEnabled(true);
    setProfessional(e.target.value);
  };

  const handleSetDayoff = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (appointmentsOnDay?.length > 0) {
      toast.error('Já existe agendamento ou almoço para este barbeiro nesta data.');
      return;
    }

    setSubmitting(true);
    const dayOffId = buildDayOffId({ date, professional });
    const result = await setDocument(
      { date, professional, professional_id: professional === 'Carlos' ? 1 : 2, dayOffId, updatedAt: new Date() },
      dayOffId,
    );
    setSubmitting(false);

    if (!result?.success) {
      toast.error(result?.error || 'Não foi possível agendar a folga. Tente novamente.');
    } else {
      toast.success('Folga agendada com sucesso.');
      setProfessional('');
      setDate('');
      setEnabled(false);
    }
  };

  const handleCancel = async (id) => {
    const deleted = await deleteData('day-off', id);
    if (!deleted) toast.error('Não foi possível cancelar a folga. Tente novamente.');
    else toast.success('Folga cancelada.');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Form */}
      <form onSubmit={handleSetDayoff} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="dayoff-pro" className="text-sm font-medium text-text-secondary">Barbeiro</label>
            <select
              id="dayoff-pro"
              required
              value={professional}
              onChange={handleProfessionalChange}
              disabled={submitting}
              className="rounded-md border border-border px-3.5 py-2.5 text-base bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus appearance-none"
            >
              <option value="">Selecione</option>
              {PROFESSIONALS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="dayoff-date" className="text-sm font-medium text-text-secondary">Data</label>
            <input
              id="dayoff-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={formatLocalDateInput()}
              disabled={!enabled || submitting}
              required
              className="rounded-md border border-border px-3.5 py-2.5 text-base bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" loading={submitting} disabled={!professional || !date}>
            Agendar folga
          </Button>
        </div>
      </form>

      {/* Listagem */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="dayoff-filter" className="text-sm font-medium text-text-secondary">Filtrar por data</label>
          <input
            id="dayoff-filter"
            type="date"
            value={dayOffDate}
            onChange={(e) => setDayOffDate(e.target.value)}
            className="rounded-md border border-border px-3.5 py-2.5 text-sm bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus w-full sm:w-48"
          />
        </div>

        {documents && documents.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {documents.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 bg-bg-secondary rounded-lg px-4 py-3">
                <span className="text-sm text-text-primary">
                  <span className="font-semibold">{d.professional}</span>
                  <span className="text-text-muted"> — {formatLocalDateDisplay(d.date)}</span>
                </span>
                <button
                  type="button"
                  onClick={() => handleCancel(d.id)}
                  aria-label="Cancelar folga"
                  className="text-text-muted hover:text-danger transition-colors duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus rounded"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-text-muted py-2">Nenhuma folga encontrada para esta data.</p>
        )}
      </div>
    </div>
  );
}
